import { useCallback, useEffect, useRef, useState } from "react";

import { Badge, Button } from "../../components/ui";
import { errorMessage } from "../../lib/api";
import { formatBytes } from "../../lib/format";
import {
  cancelUpload,
  forgetResume,
  uploadFile,
  type InitiateFn,
  type UploadProgress,
} from "../../lib/uploader";

export interface AttachedFile {
  key: string;
  name: string;
  size: number;
  file?: File;
  progress: UploadProgress;
}

const ACCEPT = ".pdf,.tif,.tiff,.png,.jpg,.jpeg,.dwg,.dxf,.dwf,.dwfx,.plt,.hpgl,.hpg";

const stateLabels: Record<
  UploadProgress["state"],
  { label: string; tone: "neutral" | "brand" | "ok" | "warn" | "danger" }
> = {
  queued: { label: "Waiting", tone: "neutral" },
  uploading: { label: "Uploading", tone: "brand" },
  finishing: { label: "Finishing", tone: "brand" },
  checking: { label: "Security check", tone: "warn" },
  ready: { label: "Ready", tone: "ok" },
  rejected: { label: "Not accepted", tone: "danger" },
  error: { label: "Upload stopped", tone: "danger" },
  cancelled: { label: "Cancelled", tone: "neutral" },
};

export function FileUploader({
  maxBytes,
  chunkBytes,
  files,
  onChange,
  error,
  initiate,
  accept = ACCEPT,
  hint = "PDF, TIFF, PNG, JPEG, DWG, DXF, DWF or PLT.",
}: {
  maxBytes: number;
  chunkBytes: number;
  files: AttachedFile[];
  onChange: (updater: (prev: AttachedFile[]) => AttachedFile[]) => void;
  error?: string;
  initiate?: InitiateFn;
  accept?: string;
  hint?: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const controllers = useRef(new Map<string, AbortController>());
  const [dragging, setDragging] = useState(false);

  const update = useCallback(
    (key: string, patch: Partial<AttachedFile>) =>
      onChange((prev) => prev.map((f) => (f.key === key ? { ...f, ...patch } : f))),
    [onChange],
  );

  const start = useCallback(
    (item: AttachedFile) => {
      if (!item.file) return;
      const controller = new AbortController();
      controllers.current.set(item.key, controller);
      uploadFile(
        item.file,
        (progress) => update(item.key, { progress }),
        controller.signal,
        chunkBytes,
        initiate,
      ).catch((err: unknown) => {
        if ((err as Error).name === "AbortError") return;
        update(item.key, {
          progress: { ...item.progress, state: "error", message: errorMessage(err) },
        });
      });
    },
    [chunkBytes, update, initiate],
  );

  useEffect(() => {
    const current = controllers.current;
    return () => current.forEach((c) => c.abort());
  }, []);

  const addFiles = (list: FileList | File[]) => {
    const added: AttachedFile[] = [];
    for (const file of Array.from(list)) {
      const key = `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2)}`;
      if (file.size > maxBytes) {
        added.push({
          key,
          name: file.name,
          size: file.size,
          progress: {
            state: "rejected",
            sent: 0,
            total: file.size,
            message: `Larger than the ${formatBytes(maxBytes)} limit.`,
          },
        });
        continue;
      }
      if (file.size === 0) {
        added.push({
          key,
          name: file.name,
          size: 0,
          progress: { state: "rejected", sent: 0, total: 0, message: "This file is empty." },
        });
        continue;
      }
      added.push({
        key,
        name: file.name,
        size: file.size,
        file,
        progress: { state: "queued", sent: 0, total: file.size },
      });
    }
    onChange((prev) => [...prev, ...added]);
    added.filter((a) => a.file).forEach(start);
  };

  const remove = async (item: AttachedFile) => {
    controllers.current.get(item.key)?.abort();
    controllers.current.delete(item.key);
    if (item.progress.uploadId && item.progress.state !== "ready")
      await cancelUpload(item.progress.uploadId);
    if (item.file) forgetResume(item.file);
    onChange((prev) => prev.filter((f) => f.key !== item.key));
  };

  return (
    <div className="flex flex-col gap-3">
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          if (e.dataTransfer.files.length) addFiles(e.dataTransfer.files);
        }}
        className={`flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-6 text-center ${
          dragging
            ? "border-brand-500 bg-brand-50"
            : error
              ? "border-danger"
              : "border-line bg-white"
        }`}
      >
        <p className="font-semibold">Drag files here</p>
        <p className="text-sm text-ink-muted">
          {hint} Up to {formatBytes(maxBytes)} per file.
        </p>
        <Button variant="secondary" onClick={() => inputRef.current?.click()}>
          Browse for files
        </Button>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={accept}
          className="sr-only"
          aria-label="Choose files to attach"
          onChange={(e) => {
            if (e.target.files) addFiles(e.target.files);
            e.target.value = "";
          }}
        />
      </div>
      {error && (
        <p className="text-sm font-medium text-danger" role="alert">
          {error}
        </p>
      )}
      {files.length > 0 && (
        <ul
          className="divide-y divide-line rounded-lg border border-line bg-white"
          aria-label="Attached files"
        >
          {files.map((f) => {
            const s = stateLabels[f.progress.state];
            const pct = f.size ? Math.round((f.progress.sent / f.size) * 100) : 100;
            return (
              <li key={f.key} className="flex flex-col gap-2 p-3 sm:flex-row sm:items-center">
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium" title={f.name}>
                    {f.name}
                  </p>
                  <p className="text-sm text-ink-muted">
                    {formatBytes(f.size)} · <Badge tone={s.tone}>{s.label}</Badge>
                    {f.progress.message && <span className="ml-1">{f.progress.message}</span>}
                  </p>
                  {(f.progress.state === "uploading" || f.progress.state === "finishing") && (
                    <progress
                      className="mt-1 h-2 w-full accent-brand-600"
                      max={100}
                      value={pct}
                      aria-label={`Upload progress for ${f.name}`}
                    >
                      {pct}%
                    </progress>
                  )}
                </div>
                <div className="flex gap-2">
                  {f.progress.state === "error" && f.file && (
                    <Button variant="secondary" onClick={() => start(f)}>
                      Retry
                    </Button>
                  )}
                  <Button variant="ghost" onClick={() => remove(f)} aria-label={`Remove ${f.name}`}>
                    {f.progress.state === "uploading" ? "Cancel" : "Remove"}
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
